/** biome-ignore-all lint/a11y/noStaticElementInteractions: TODO fix this */
/** biome-ignore-all lint/a11y/useKeyWithClickEvents: TODO fix this */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDesignTokensStore } from "@/components/DesignTokens/store/designTokensStore";
import type { ColorGroup, ColorToken } from "@/components/DesignTokens/types";
import {
  filterColorGroups,
  getDisplayColor,
} from "@/components/RightStyle/TailwindStylingPanel/components/input/color-picker/utils/colorFormatUtils";
import { SearchInput } from "@/components/ui/search-input";
import { cn } from "@/lib/utils";
import { useTypedSelector } from "@/store/store";
import { useDarkModeStore } from "@/store/zustand/darkModeStore";
import { convertOptionCssVariableToTailwindClass } from "@/tailwind/tailwindUtils";
import type { ComboBoxOptionType } from "./input";
import ColorSwatch from "./input/ColorSwatch";

interface LibraryColorPanelProps {
  onColorSelected: (option: ComboBoxOptionType) => void;
  className?: string;
  comboboxOptions?: Array<{ tailwind: string; css: string }>;
  selectedColor?: string;
}

const LibraryColorPanel: React.FC<LibraryColorPanelProps> = ({
  onColorSelected,
  className,
  comboboxOptions = [],
  selectedColor,
}) => {
  const cssCustomProperties = useTypedSelector(
    (state) => state.iframe.cssCustomProperties,
  );

  const [searchQuery, setSearchQuery] = useState("");

  const { designTokenColorGroups, designTokenColors, loading } =
    useDesignTokensStore((state) => ({
      designTokenColorGroups: state.mergedColorGroups, // Filtered based on view mode
      designTokenColors: state.mergedColors, // Filtered based on view mode
      loading: state.loading,
    }));

  const isCanvasDarkMode = useDarkModeStore((state) => state.isCanvasDarkMode);
  const setViewDarkMode = useDarkModeStore((state) => state.setViewDarkMode);

  // Sync view dark mode with canvas dark mode when canvas dark mode changes
  useEffect(() => {
    setViewDarkMode(isCanvasDarkMode);
  }, [isCanvasDarkMode, setViewDarkMode]);

  const colorGroups = useMemo(() => {
    const groups: ColorGroup[] = [];

    if (designTokenColorGroups) {
      groups.push(...designTokenColorGroups);
    }

    // Add ungrouped colors as a separate "Colors" group
    if (designTokenColors && designTokenColors.length > 0) {
      // Filter out colors with errors
      const validUngroupedColors = designTokenColors.filter(
        (token) => !token.hasError,
      );

      // Only add the Colors group if there are valid ungrouped colors
      if (validUngroupedColors.length > 0) {
        groups.push({
          name: "Colors",
          isExpanded: false,
          colors: validUngroupedColors,
        });
      }
    }

    return groups;
  }, [designTokenColorGroups, designTokenColors]);

  const handleColorSelection = (option: ComboBoxOptionType) => {
    onColorSelected(option);
  };

  // Use shared filtering logic from color store
  const filteredColorGroups = useMemo(() => {
    return filterColorGroups(colorGroups, searchQuery);
  }, [colorGroups, searchQuery]);

  const hasAnyExistingData =
    (designTokenColorGroups?.length || 0) > 0 ||
    (designTokenColors?.length || 0) > 0;

  if (loading && !hasAnyExistingData) {
    return (
      <div className={cn("p-4 text-center", className)}>
        <div className="text-secondary-n-500 text-sm">
          Loading colors from your Tailwind configuration...
        </div>
      </div>
    );
  }

  if (colorGroups.length === 0) {
    return (
      <div className={cn("p-4 text-center", className)}>
        <div className="text-secondary-n-500 text-sm">
          No custom colors found in your Tailwind configuration.
        </div>
        <div className="text-secondary-n-600 text-2xs mt-1">
          Add custom colors to your tailwind.config.js to see them here.
        </div>
      </div>
    );
  }

  return (
    <div className={cn("", className)}>
      <div className="border-y border-secondary-n-900 py-2 space-y-2">
        <div className="flex items-center gap-2">
          <SearchInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery("")}
            className="flex-1 h-7"
          />
        </div>
      </div>

      {/* Color Groups */}
      <div className="max-h-80 overflow-y-auto hide-scrollbar">
        {filteredColorGroups.length === 0 ? (
          <div className="p-4 text-center">
            <div className="text-secondary-n-500 text-sm">
              No colors match your search.
            </div>
          </div>
        ) : (
          <div className="py-2">
            {filteredColorGroups.map((group) => (
              <ColorGroupComponent
                key={group.name}
                group={group}
                onColorSelected={handleColorSelection}
                comboboxOptions={comboboxOptions}
                cssCustomProperties={cssCustomProperties}
                selectedColor={selectedColor}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface ColorItemProps {
  color: ColorToken;
  onSelected: (option: ComboBoxOptionType, color?: ColorToken) => void;
  comboboxOptions: Array<{ tailwind: string; css: string }>;
  cssCustomProperties?: {
    root: Record<string, string>;
    dark: Record<string, string>;
  } | null;
  isSelected?: boolean;
}

const ColorItem: React.FC<ColorItemProps> = ({
  color,
  onSelected,
  comboboxOptions,
  cssCustomProperties,
  isSelected,
}) => {
  const itemRef = useRef<HTMLDivElement>(null);

  // Scroll into view on initial render if selected
  const hasScrolledRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: don't want to re-run this effect when the isSelected changes
  useEffect(() => {
    if (isSelected && itemRef.current && !hasScrolledRef.current) {
      hasScrolledRef.current = true;
      itemRef.current.scrollIntoView({ block: "center", behavior: "instant" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use getDisplayColor to properly resolve CSS variables
  const displayColor = useMemo(() => {
    return getDisplayColor(color, cssCustomProperties, comboboxOptions);
  }, [color, cssCustomProperties, comboboxOptions]);

  const handleClick = () => {
    // Use the resolved tailwindClass which includes proper suffixes
    const tailwindClass = `bg-${color.tailwindClass}`;

    // Find the matching option in comboboxOptions to get the proper CSS value
    const matchingOption = comboboxOptions.find(
      (option) => option.tailwind === tailwindClass,
    );

    const option: ComboBoxOptionType = {
      tailwind: tailwindClass,
      css:
        matchingOption?.css ||
        `background-color: ${color.hexColor || color.color}`,
    };

    // Convert CSS variables to proper Tailwind classes (e.g., bg-[var(--border)] -> bg-border)
    const convertedOption = convertOptionCssVariableToTailwindClass(option);

    onSelected(convertedOption, color);
  };

  return (
    <div
      ref={itemRef}
      className={cn(
        "w-full flex items-center justify-between p-2 py-1 hover:bg-background-tertiary rounded transition-colors duration-150 text-left cursor-pointer",
        isSelected && "bg-background-tertiary",
      )}
      onClick={handleClick}
    >
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-4 h-4 p-0.5 flex items-center justify-center relative">
          {color.isFallback ? (
            // Show transparent swatch with question mark for fallback tokens
            <div className="w-full h-full relative">
              <ColorSwatch color="transparent" className="opacity-30" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[10px] font-bold text-text-secondary">
                  ?
                </span>
              </div>
            </div>
          ) : (
            <ColorSwatch color={displayColor} />
          )}
        </div>
        <span className="text-xs text-secondary-n-100 font-medium pr-2 truncate capitalize">
          {color.name}
        </span>
      </div>
      <span className="text-xs text-secondary-n-500 font-mono uppercase pl-2 shrink-0">
        {displayColor}
      </span>
    </div>
  );
};

interface ColorGroupComponentProps {
  group: ColorGroup;
  onColorSelected: (option: ComboBoxOptionType, color?: ColorToken) => void;
  comboboxOptions: Array<{ tailwind: string; css: string }>;
  cssCustomProperties?: {
    root: Record<string, string>;
    dark: Record<string, string>;
  } | null;
  selectedColor?: string;
}

const ColorGroupComponent: React.FC<ColorGroupComponentProps> = ({
  group,
  onColorSelected,
  comboboxOptions,
  cssCustomProperties,
  selectedColor,
}) => {
  if (group.colors.length === 0) return null;

  return (
    <div className="mb-2">
      <h3 className="text-xs font-medium text-text-secondary my-1 px-2 capitalize">
        {group.name}
      </h3>
      <div className="">
        {group.colors.map((color) => (
          <ColorItem
            key={color.name}
            color={color}
            onSelected={onColorSelected}
            comboboxOptions={comboboxOptions}
            cssCustomProperties={cssCustomProperties}
            isSelected={selectedColor === color.tailwindClass}
          />
        ))}
      </div>
    </div>
  );
};

export default React.memo(LibraryColorPanel);
